import { ViewAll } from './../ViewAll/ViewAll';
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EsGenreTag, GenreTag } from '../EsCards/EsGenreTag';
import { INebulaSuggestion } from '../../redux/project/types';
import { isEmpty } from '../../utils/lodash';
import { useSearchParams } from 'react-router-dom';

interface ITagParams {
    subcategory_tag?: string;
}

export interface IMainCarousel extends Partial<Omit<GenreTag, 'genre'>> {
    list?: string[];
    carouselTitle?: string;
    onClickViewAll?: () => void;
    onSelectTag?: (tag: any) => void;
    disableTitle?: boolean;
    style?: React.CSSProperties;
    listStyle?: React.CSSProperties;
    suggestion?: INebulaSuggestion[];
    type?: 'list' | 'suggestion';
    selection?: INebulaSuggestion;
    disableViewAll?: boolean;
    titleStyle?: React.CSSProperties;
    preselect?: boolean;
    disregardParams?: boolean;
    initialValue?: string;
    additionalDataCollection?: string[] | number[];
    onTagPressed?: (tag: string) => void;
    /**lays out the tags in a rectangular contained format */
    groupLayout?: boolean;
}

const MainTagCarousel: React.FC<IMainCarousel> = props => {
    const {
        list,
        carouselTitle,
        onClickViewAll,
        onSelectTag,
        disableTitle,
        style,
        listStyle,
        type,
        suggestion,
        selection,
        disableViewAll,
        titleStyle,
        preselect,
        disregardParams,
        initialValue,
        additionalDataCollection,
        onTagPressed,
        groupLayout,
    } = props;
    const { themeStyle } = useThemeContext();
    const [selectedTag, setSelectedTag] = useState<any>(list && list[-1]);
    const [searchParams, setSearchParams] = useSearchParams();
    const params: ITagParams = Object.fromEntries([...searchParams]) as unknown as ITagParams;

    const handleOnSelectTag = (tag: any) => {
        if (onSelectTag) {
            onSelectTag(tag);
        }
        setSelectedTag(tag);
        if (!disregardParams)
            setSearchParams(() => ({
                ...params,
                subcategory_tag: tag,
            }));
    };

    const handlSetInitialTag = useCallback(() => {
        if (initialValue) {
            setSelectedTag(initialValue);
            onSelectTag && onSelectTag(initialValue);
        } else {
            if (!disregardParams && params && params['subcategory_tag']) {
                setSelectedTag(params['subcategory_tag']);
                if (onSelectTag) {
                    onSelectTag(params['subcategory_tag']);
                }
            } else if (preselect && !!list) {
                setSelectedTag(list[0]);
                if (onSelectTag && !!params['subcategory_tag']) {
                    onSelectTag(params['subcategory_tag']);
                }
            }
        }
    }, [preselect, params, list, onSelectTag, disregardParams, initialValue]);

    useEffect(() => {
        handlSetInitialTag();
    }, [handlSetInitialTag]);

    return (
        <Box
            style={{
                width: '100%',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: '37px',
                ...style,
            }}>
            {!disableTitle && (
                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography
                        sx={{
                            textAlign: 'left',
                            fontSize: {
                                xs: 16,
                                lg: 24,
                            },
                            fontStyle: 'normal',
                            fontWeight: '600',
                            color: theme[themeStyle].filters_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'capitalize',
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 1, //numOfLines
                            ...titleStyle,
                        }}>
                        {carouselTitle}
                    </Typography>
                    {!disableViewAll && <ViewAll onClickViewAll={onClickViewAll} themeStyle={themeStyle} />}
                </Box>
            )}
            {!groupLayout && (
                <Box
                    style={{
                        width: '100%',
                        height: 'fit-content',
                        display: 'flex',
                        overflowX: 'scroll',
                        gap: '16px',
                        ...listStyle,
                    }}>
                    {type === 'suggestion'
                        ? suggestion &&
                          !isEmpty(suggestion) &&
                          suggestion.map((item, index) => (
                              <EsGenreTag
                                  genre={item.title}
                                  key={index.toString()}
                                  {...props}
                                  onClickGenre={() => {
                                      handleOnSelectTag(item);
                                  }}
                                  active={selection && selection.title === item.title}
                                  additionalData={additionalDataCollection && additionalDataCollection[index]}
                              />
                          ))
                        : list &&
                          !isEmpty(list) &&
                          list.map((item, index) => (
                              <EsGenreTag
                                  genre={item}
                                  key={index.toString()}
                                  {...props}
                                  onClickGenre={() => {
                                      handleOnSelectTag(item);
                                      onTagPressed && onTagPressed(item);
                                  }}
                                  active={selectedTag === item}
                                  additionalData={additionalDataCollection && additionalDataCollection[index]}
                              />
                          ))}
                </Box>
            )}
            {groupLayout && (
                <Box
                    sx={{
                        width: 'fit-content',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: '4px',
                        borderRadius: 2.5,
                        overflowX: 'scroll',
                        backgroundColor: theme[themeStyle].dropdown_black,
                        ...listStyle,
                    }}>
                    {type === 'suggestion'
                        ? suggestion &&
                          !isEmpty(suggestion) &&
                          suggestion.map((item, index) => (
                              <EsGenreTag
                                  genre={item.title}
                                  key={index.toString()}
                                  {...props}
                                  onClickGenre={() => {
                                      handleOnSelectTag(item);
                                  }}
                                  groupLayout
                                  active={selection && selection.title === item.title}
                                  additionalData={additionalDataCollection && additionalDataCollection[index]}
                              />
                          ))
                        : list &&
                          !isEmpty(list) &&
                          list.map((item, index) => (
                              <EsGenreTag
                                  genre={item}
                                  key={index.toString()}
                                  {...props}
                                  onClickGenre={() => {
                                      handleOnSelectTag(item);
                                      onTagPressed && onTagPressed(item);
                                  }}
                                  groupLayout
                                  active={selectedTag === item}
                                  additionalData={additionalDataCollection && additionalDataCollection[index]}
                              />
                          ))}
                </Box>
            )}
        </Box>
    );
};

export { MainTagCarousel };
